export interface PollUntilReadyOptions {
  /** Hard cap on total wait time across all polls. Default: 10 minutes. */
  maxWindowMs?: number
  /** Called before each wait — surface progress in the UI. */
  onWait?: (info: { waitMs: number; elapsedMs: number; attempt: number }) => void
}

interface PollResultLike {
  retryAfterMs?: number
}

/**
 * Polls `fn` until `isReady` returns true. Each non-ready result must carry a
 * `retryAfterMs` field telling us how long to wait before polling again.
 */
export async function pollUntilReady<T extends PollResultLike, R extends T>(
  fn: () => Promise<T>,
  isReady: (result: T) => result is R,
  options: PollUntilReadyOptions = {},
): Promise<R> {
  const { maxWindowMs = 10 * 60 * 1000, onWait } = options

  const start = Date.now()
  let attempt = 0
  while (true) {
    const result = await fn()
    if (isReady(result)) return result

    attempt += 1
    const waitMs = result.retryAfterMs ?? 10_000
    const elapsedMs = Date.now() - start
    if (elapsedMs + waitMs > maxWindowMs) {
      throw new Error(`pollUntilReady exceeded ${maxWindowMs}ms window`)
    }
    onWait?.({ waitMs, elapsedMs, attempt })
    await new Promise(r => setTimeout(r, waitMs))
  }
}
